import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminApiError } from '../../api/admin.ts';
import ImpersonateUsers from '../../components/admin/impersonate-users.tsx';
import { discountAmountLabel } from './discounts.tsx';
import { Alert, Button, LoadingBlock, Select, TextArea, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { statusBadge } from './companies.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { PLANS, isPlanId } from '@shared/constants/index.ts';
import { heardAboutLabel } from '@shared/heard-about.ts';

/**
 * One company, read live from its shard: account state, billing, and the
 * operator actions. Every action writes the admin audit trail.
 */

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleString() : '—';
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[color:var(--color-line)] py-2 text-sm last:border-b-0">
      <span className="text-[color:var(--color-muted)]">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

function ContactLink({ href, children }: { href: string | null; children: string | null }) {
  if (!href || !children) return <>—</>;
  return (
    <a href={href} className="text-[color:var(--color-brand)] hover:underline">
      {children}
    </a>
  );
}

export default function AdminCompanyDetailPage() {
  const { companyId = '' } = useParams();
  const queryClient = useQueryClient();

  const company = useQuery({
    queryKey: ['admin', 'company', companyId],
    queryFn: ({ signal }) => adminApi.getCompany(companyId, signal),
    enabled: companyId !== '',
  });
  useDocumentTitle(company.data ? `${company.data.name} · Admin` : 'Company · Admin');

  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [trialDays, setTrialDays] = useState('30');
  const [newPlan, setNewPlan] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [discountId, setDiscountId] = useState('');

  const discounts = useQuery({
    queryKey: ['admin', 'discounts'],
    queryFn: ({ signal }) => adminApi.listDiscounts(signal),
  });
  const paymentMethod = useQuery({
    queryKey: ['admin', 'company', companyId, 'payment-method'],
    queryFn: ({ signal }) => adminApi.getPaymentMethod(companyId, signal),
    enabled: companyId !== '',
    staleTime: 5 * 60 * 1000,
  });

  async function run(action: string, work: () => Promise<unknown>, done: string) {
    setBusyAction(action);
    setError(null);
    setNotice(null);
    try {
      await work();
      setNotice(done);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'company', companyId] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
    } catch (caught) {
      setError(caught instanceof AdminApiError ? caught.message : 'That action failed. Please try again.');
    } finally {
      setBusyAction(null);
    }
  }

  async function handleAddNote(event: FormEvent) {
    event.preventDefault();
    const body = noteBody.trim();
    if (!body) return;
    await run('note', () => adminApi.addNote(companyId, body), 'Note added.');
    setNoteBody('');
  }

  if (company.isLoading) return <PortalReadState title="Company" label="Loading company…" />;
  if (company.isError || !company.data) {
    return (
      <PortalReadState
        title="Company"
        error="That company could not be loaded. It may have been terminated."
        retry={() => company.refetch()}
      >
        <div className="mt-4">
          <Link to="/admin/companies" className="font-semibold text-[color:var(--color-brand)] hover:underline">
            Back to companies
          </Link>
        </div>
      </PortalReadState>
    );
  }

  const data = company.data;
  const planLabel = isPlanId(data.plan) ? PLANS[data.plan].name : 'Not chosen';

  return (
    <div className="portal-page">
      <WorkPage
        title={data.name}
        description={data.ownerEmail ?? undefined}
        actions={statusBadge(data.billingStatus, data.terminationRequestedAt !== null)}
      >
        <div className="mb-2">
          <Link to="/admin/companies" className="text-sm text-[color:var(--color-muted)] hover:underline">
            ← Companies
          </Link>
        </div>

        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success">{notice}</Alert>
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-2">
          <WorkSection title="Contact">
            <Row label="Owner">{data.ownerName ?? '—'}</Row>
            <Row label="Owner email">
              <ContactLink href={data.ownerEmail ? `mailto:${data.ownerEmail}` : null}>{data.ownerEmail}</ContactLink>
            </Row>
            <Row label="Owner phone">
              <ContactLink href={data.ownerPhone ? `tel:${data.ownerPhone}` : null}>{data.ownerPhone}</ContactLink>
            </Row>
            <Row label="Company email">
              <ContactLink href={data.email ? `mailto:${data.email}` : null}>{data.email}</ContactLink>
            </Row>
            <Row label="Company phone">
              <ContactLink href={data.phone ? `tel:${data.phone}` : null}>{data.phone}</ContactLink>
            </Row>
          </WorkSection>

          <WorkSection title="Account">
            <Row label="Created">{formatDateTime(data.createdAt)}</Row>
            <Row label="Last activity">{formatDateTime(data.lastActivityAt)}</Row>
            <Row label="Language">{data.language ?? '—'}</Row>
            <Row label="Timezone">{data.timezone}</Row>
            <Row label="Seats (active users)">{data.seats}</Row>
            <Row label="Locations">{data.locations}</Row>
            <Row label="Shard">{data.shardId}</Row>
            {data.terminationRequestedAt ? (
              <Row label="Termination requested">{formatDateTime(data.terminationRequestedAt)}</Row>
            ) : null}
            {data.attribution ? (
              <>
                <Row label="Landing page">{data.attribution.landingPath ?? '—'}</Row>
                <Row label="Referred by">{data.attribution.referrer ?? '—'}</Row>
                {data.attribution.utmSource || data.attribution.utmMedium || data.attribution.utmCampaign ? (
                  <Row label="Campaign">
                    {[data.attribution.utmSource, data.attribution.utmMedium, data.attribution.utmCampaign]
                      .filter(Boolean)
                      .join(' / ')}
                  </Row>
                ) : null}
                {data.attribution.utmTerm ? <Row label="Search term">{data.attribution.utmTerm}</Row> : null}
                {data.attribution.utmContent ? <Row label="Ad content">{data.attribution.utmContent}</Row> : null}
              </>
            ) : null}
            {data.heardAbout ? (
              <Row label="How they heard">
                {heardAboutLabel(data.heardAbout)}
                {data.heardAboutDetail ? ` — ${data.heardAboutDetail}` : ''}
              </Row>
            ) : null}
          </WorkSection>

          <WorkSection title="Billing">
            <Row label="Status">{data.billingStatus}</Row>
            <Row label="Plan">
              {planLabel}
              {data.billingInterval === 'year' ? ' (yearly)' : data.subscribed ? ' (monthly)' : ''}
            </Row>
            <Row label="MRR">{data.mrrCents > 0 ? formatCents(data.mrrCents, data.currencyCode) : '—'}</Row>
            {data.discountPercent ? <Row label="Discount">−{data.discountPercent}%</Row> : null}
            <Row label="Trial ends">
              {formatDateTime(data.trialEndsAt)}
              {data.billingStatus === 'trialing' && data.trialDaysLeft > 0 ? ` (${data.trialDaysLeft} days left)` : ''}
            </Row>
            <Row label="Period ends">{formatDateTime(data.billingPeriodEnd)}</Row>
            {data.cancelAtPeriodEnd ? <Row label="Cancels">At period end</Row> : null}
            <Row label="Card on file">
              {paymentMethod.isError ? (
                <>
                  Could not load card.{' '}
                  <Button variant="ghost" onClick={() => void paymentMethod.refetch()}>
                    Try again
                  </Button>
                </>
              ) : paymentMethod.data?.card ? (
                `${paymentMethod.data.card.brand} •••• ${paymentMethod.data.card.last4}${
                  paymentMethod.data.card.expMonth
                    ? ` (${paymentMethod.data.card.expMonth}/${paymentMethod.data.card.expYear})`
                    : ''
                }`
              ) : (
                (data.paymentMethodLabel ?? '—')
              )}
            </Row>
            {discounts.isError ? (
              <Alert tone="error">
                Discount options could not be loaded.{' '}
                <Button variant="ghost" onClick={() => void discounts.refetch()}>
                  Try again
                </Button>
              </Alert>
            ) : null}
            <Row label="Locked out">{data.locked ? 'Yes — access window closed' : 'No'}</Row>

            <div className="mt-4 flex flex-wrap items-end gap-2">
              <div className="min-w-52 flex-1">
                <Select
                  label="Grant a discount"
                  options={[
                    { value: '', label: 'Choose a discount…' },
                    ...(discounts.data ?? []).map((discount) => ({
                      value: discount.id,
                      label: `${discount.name} — ${discountAmountLabel(discount)}`,
                    })),
                  ]}
                  value={discountId}
                  onChange={(event) => setDiscountId(event.target.value)}
                />
              </div>
              <div className="pb-4 flex gap-2">
                <Button
                  busy={busyAction === 'discount'}
                  disabled={!discountId}
                  onClick={() =>
                    void run(
                      'discount',
                      () => adminApi.grantDiscount(companyId, discountId),
                      data.subscribed
                        ? 'Discount applied to the live subscription.'
                        : 'Discount stored — their first checkout applies it.',
                    )
                  }
                >
                  Grant
                </Button>
                {data.discountPercent || data.subscribed ? (
                  <Button
                    variant="ghost"
                    busy={busyAction === 'discount-remove'}
                    onClick={() =>
                      void run('discount-remove', () => adminApi.removeDiscount(companyId), 'Discount removed.')
                    }
                  >
                    Remove discount
                  </Button>
                ) : null}
              </div>
            </div>
          </WorkSection>
        </div>

        <WorkSection title="Actions">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <form
              className="flex items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const days = Number.parseInt(trialDays, 10);
                if (!Number.isFinite(days) || days < 1) return;
                void run('extend', () => adminApi.extendTrial(companyId, days), `Trial extended by ${days} days.`);
              }}
            >
              <div className="w-28">
                <TextInput
                  label="Days"
                  type="number"
                  min={1}
                  max={365}
                  value={trialDays}
                  onChange={(event) => setTrialDays(event.target.value)}
                />
              </div>
              <div className="pb-4">
                <Button type="submit" busy={busyAction === 'extend'}>
                  Extend trial
                </Button>
              </div>
            </form>

            {data.billingStatus === 'trialing' && data.trialDaysLeft > 0 ? (
              <div className="pb-4">
                <Button
                  variant="danger"
                  busy={busyAction === 'end-trial'}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `End ${data.name}'s trial now? Paid features lock immediately. This does not switch them to the Free plan. You can extend the trial again afterwards.`,
                      )
                    ) {
                      return;
                    }
                    void run('end-trial', () => adminApi.endTrial(companyId), 'Trial ended.');
                  }}
                >
                  End trial
                </Button>
              </div>
            ) : null}

            {data.subscribed ? (
              <form
                className="flex items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!newPlan || newPlan === data.plan) return;
                  void run('plan', () => adminApi.changePlan(companyId, newPlan), 'Plan changed.');
                }}
              >
                <div className="w-40">
                  <Select
                    label="Move to plan"
                    options={[
                      { value: '', label: 'Choose…' },
                      { value: 'starter', label: PLANS.starter.name },
                      { value: 'pro', label: PLANS.pro.name },
                      { value: 'business', label: PLANS.business.name },
                    ]}
                    value={newPlan}
                    onChange={(event) => setNewPlan(event.target.value)}
                  />
                </div>
                <div className="pb-4">
                  <Button type="submit" busy={busyAction === 'plan'} disabled={!newPlan || newPlan === data.plan}>
                    Change plan
                  </Button>
                </div>
              </form>
            ) : null}

            <div className="flex flex-wrap gap-2 pb-4">
              {data.billingCustomerId ? (
                <Button
                  busy={busyAction === 'card-link'}
                  onClick={() =>
                    void run(
                      'card-link',
                      () => adminApi.sendCardUpdateLink(companyId),
                      'Card-update link emailed to the owner.',
                    )
                  }
                >
                  Email card-update link
                </Button>
              ) : null}
              {data.subscribed && !data.cancelAtPeriodEnd ? (
                <Button
                  variant="danger"
                  busy={busyAction === 'cancel'}
                  onClick={() => {
                    if (!window.confirm(`Cancel ${data.name}'s subscription at the end of the paid period?`)) return;
                    void run(
                      'cancel',
                      () => adminApi.cancelSubscription(companyId),
                      'Subscription set to cancel at period end.',
                    );
                  }}
                >
                  Cancel subscription
                </Button>
              ) : null}
              {data.subscribed && data.cancelAtPeriodEnd ? (
                <Button
                  busy={busyAction === 'resume'}
                  onClick={() =>
                    void run('resume', () => adminApi.resumeSubscription(companyId), 'Subscription resumed.')
                  }
                >
                  Resume subscription
                </Button>
              ) : null}
              {data.terminationRequestedAt ? (
                <Button
                  busy={busyAction === 'termination'}
                  onClick={() =>
                    void run('termination', () => adminApi.clearTermination(companyId), 'Termination request cleared.')
                  }
                >
                  Undo termination request
                </Button>
              ) : (
                <Button
                  variant="danger"
                  busy={busyAction === 'termination'}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Request termination for ${data.name}? Their data is deleted by the next cron run once the six-month retention window has passed.`,
                      )
                    ) {
                      return;
                    }
                    void run(
                      'termination',
                      () => adminApi.requestTermination(companyId),
                      'Termination requested. The retention window still applies.',
                    );
                  }}
                >
                  Request termination
                </Button>
              )}
            </div>
          </div>
        </WorkSection>

        <WorkSection title="Sign in as a user">
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">
            Opens the app as that person, with their permissions, for one hour. Recorded here and in this company&apos;s
            own activity log.
          </p>
          <ImpersonateUsers companyId={companyId} companyName={data.name} />
        </WorkSection>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <WorkSection title="Notes">
            <form onSubmit={handleAddNote} className="mb-4">
              <TextArea
                label="Add a note"
                rows={2}
                value={noteBody}
                onChange={(event) => setNoteBody(event.target.value)}
                placeholder="Spoke to the owner about…"
              />
              <Button type="submit" busy={busyAction === 'note'} disabled={!noteBody.trim()}>
                Add note
              </Button>
            </form>
            {data.notes.length === 0 ? (
              <p className="text-sm text-[color:var(--color-muted)]">No notes yet.</p>
            ) : (
              <ul className="space-y-3">
                {data.notes.map((note) => (
                  <li key={note.id} className="border-b border-[color:var(--color-line)] pb-3 text-sm last:border-b-0">
                    <p className="whitespace-pre-wrap">{note.body}</p>
                    <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                      {note.adminEmail} · {formatDateTime(note.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </WorkSection>

          <WorkSection title="Admin activity">
            {data.adminAudit.length === 0 ? (
              <p className="text-sm text-[color:var(--color-muted)]">No admin actions on this company yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.adminAudit.map((event) => (
                  <li key={event.id} className="text-sm">
                    <span className="font-medium">{event.action.replace(/_/g, ' ')}</span>
                    {event.detail ? <span className="text-[color:var(--color-muted)]"> — {event.detail}</span> : null}
                    <div className="text-xs text-[color:var(--color-muted)]">
                      {event.adminEmail} · {formatDateTime(event.createdAt)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </WorkSection>
        </div>
      </WorkPage>
    </div>
  );
}
